import { UsingSpan } from "pages/OptionCreate/OptionCreateContainer";

// ===== ID処理関連 =====

/**
 * オブジェクトからnew_プレフィックス付きのIDを除去する
 */
const removeNewPrefix = <T extends { id?: string }>(obj: T): Omit<T, "id"> | T => {
  if (!obj.id || !obj.id.startsWith("new_")) {
    return obj;
  }

  const { id, ...objWithoutId } = obj;
  return objWithoutId;
};

// ===== UsingSpan処理関連 =====

/**
 * 単一のUsingSpanをクリーニング（new_プレフィックス除去）
 */
export const cleanSpan = (span: UsingSpan): UsingSpan => removeNewPrefix(span) as UsingSpan;

/**
 * 複数のUsingSpanをクリーニング
 */
export const cleanSpans = (spans: UsingSpan[]): UsingSpan[] => spans.map(cleanSpan);

/**
 * UsingSpanの日付をISO文字列に変換
 */
const spansToIsoString = (spans: UsingSpan[]): UsingSpan[] => {
  return spans.map((span) => ({
    ...span,
    usingStartDate: span.usingStartDate ? new Date(span.usingStartDate).toISOString() : undefined,
    usingEndDate: span.usingEndDate ? new Date(span.usingEndDate).toISOString() : undefined,
  }));
};

// ===== グローバルUsingSpan処理関連 =====

/**
 * グローバルUsingSpanが有効な場合、既存のスパンをクリア
 */
const clearUsingSpansIfGlobalUsingSpan = (spans: UsingSpan[], isGlobalUsingSpan: boolean): UsingSpan[] => {
  if (isGlobalUsingSpan) return [];
  return spans;
};

/**
 * グローバルUsingSpanを適用する際のスパン取得
 * TODO: 実装が未完了のため、空配列を返す
 */
const applySpansIfGlobalUsingSpan = (isGlobalUsingSpan: boolean): UsingSpan[] => {
  // 実装が必要
  return [];
};

// ===== Service処理関連 =====

/**
 * 単一のサービスをクリーニング（ID除去 + UsingSpans処理）
 */
export const cleanService = <
  T extends {
    id?: string;
    usingSpans?: UsingSpan[];
  },
>(
  service: T
): T => {
  const cleanedService = removeNewPrefix(service) as T;

  if (service.usingSpans) {
    return {
      ...cleanedService,
      usingSpans: spansToIsoString(cleanSpans(service.usingSpans)),
    };
  }

  return cleanedService;
};

/**
 * 複数のサービスをクリーニング
 */
export const cleanServices = <
  T extends {
    id?: string;
    usingSpans?: UsingSpan[];
  },
>(
  services: T[]
): T[] => services.map(cleanService);

// ===== UsingSpan適用ロジック =====

/**
 * グローバルUsingSpanの設定に応じて適切なUsingSpansを返す
 */
const determineUsingSpans = (
  serviceUsingSpans: UsingSpan[],
  applyGlobalUsingSpan: boolean,
  dateAssignmentType?: "FIXED" | "RESPECTIVE",
  globalUsingSpan?: UsingSpan
): UsingSpan[] => {
  if (!applyGlobalUsingSpan) {
    return serviceUsingSpans ?? [];
  }

  if (dateAssignmentType === "FIXED") {
    return globalUsingSpan ? [globalUsingSpan] : [];
  }

  // dateAssignmentType === "RESPECTIVE" の場合
  return serviceUsingSpans;
};

// ===== サービスフィルタリング・変換ロジック =====

/**
 * サービスをフィルタリング（contentがあり、checkedがtrueのもののみ）
 */
const filterValidServices = <T extends { content: string; checked?: boolean }>(services: T[]): T[] => {
  return services.filter((service) => service.content && service.checked);
};

/**
 * サービスにnameプロパティを追加（contentと同じ値）
 */
const addNameToServices = <T extends { content: string }>(services: T[]): (T & { name: string })[] => {
  return services.map((service) => ({ ...service, name: service.content }));
};

// ===== メイン関数 =====

/**
 * サービスを美化・整形して返す
 * 各処理ステップを明確に分離した実装
 */
export const beautifyServices = (
  services: {
    id: string;
    usingSpans: UsingSpan[];
    content: string;
    checked?: boolean;
  }[],
  applyGlobalUsingSpan: boolean = false,
  dateAssignmentType?: "FIXED" | "RESPECTIVE",
  globalUsingSpan?: UsingSpan
) => {
  // 1. UsingSpansの決定とサービス構造の正規化
  const processedServices = services.map((service) => ({
    usingSpans: determineUsingSpans(service.usingSpans, applyGlobalUsingSpan, dateAssignmentType, globalUsingSpan),
    content: service.content,
    checked: service.checked || false,
    id: service.id,
  }));

  // 2. 有効なサービスのみをフィルタリング
  const validServices = filterValidServices(processedServices);

  // 3. nameプロパティを追加
  const servicesWithName = addNameToServices(validServices);

  // 4. 最終的なクリーニング（new_プレフィックス除去 + ISO日付変換）
  return cleanServices(servicesWithName);
};
